import React from 'react';
import { FaTimes, FaPrint, FaBook, FaUserEdit, FaHashtag, FaTags, FaCopy, FaCalendar, FaEye, FaBookOpen } from '../../../utils/iconUtils';
import { LibraryBook, LibraryCategory } from '../../../firebase/libraryFirestoreService';

interface ViewBookModalProps {
  isOpen: boolean;
  onClose: () => void;
  book: (LibraryBook & { category?: LibraryCategory }) | null;
}

const ViewBookModal: React.FC<ViewBookModalProps> = ({ isOpen, onClose, book }) => {
  if (!isOpen || !book) return null;

  const printBook = () => {
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) return;

    const html = `
      <html>
        <head>
          <title>Print - ${book.title}</title>
          <style>
            body{font-family: Arial, Helvetica, sans-serif; padding:20px}
            .cover{width:150px;height:200px;object-fit:contain;border:1px solid #ddd}
            .meta{margin-left:18px}
            .row{display:flex;align-items:flex-start;margin-bottom:12px}
            h1{font-size:20px;margin:0 0 8px}
            p{margin:2px 0}
          </style>
        </head>
        <body>
          <div class="row">
            <div>
              ${book.imageUrl ? `<img class="cover" src="${book.imageUrl}" />` : ''}
            </div>
            <div class="meta">
              <h1>${escapeHtml(book.title || '')}</h1>
              <p><strong>Author:</strong> ${escapeHtml(book.author || '')}</p>
              <p><strong>ISBN:</strong> ${escapeHtml(book.isbn || '')}</p>
              <p><strong>Category:</strong> ${escapeHtml(book.category?.name || book.categoryId || '—')}</p>
              <p><strong>Total copies:</strong> ${book.totalCopies ?? 0}</p>
            </div>
          </div>
          <hr />
          <p>Generated: ${new Date().toLocaleString()}</p>
        </body>
      </html>
    `;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.onload = () => {
      win.focus();
      win.print();
    };
  };

  function escapeHtml(str: string) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      {/* Backdrop with your primary color */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary-50 via-primary-100 to-primary-200 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Compact Modal - ID Card Style */}
      <div className="relative bg-white rounded-2xl shadow-hard w-full max-w-lg z-10 border-4 border-white/90 overflow-hidden">
        {/* Decorative header bar using your primary color */}
        <div className="h-2 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700"></div>
        
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-primary-50 to-primary-100 border-b border-primary-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg">
                <FaBook className="h-3.5 w-3.5 text-white" />
              </div>
              <h3 className="text-sm font-bold text-primary-900">Book Details</h3>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={printBook}
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-xs font-medium rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all duration-200 shadow-sm hover:shadow"
              >
                <FaPrint className="h-3 w-3" />
                <span>Print</span>
              </button>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-primary-50 rounded-lg transition-colors"
              >
                <FaTimes className="h-3.5 w-3.5 text-primary-600 hover:text-primary-800" />
              </button>
            </div>
          </div>
        </div>

        {/* Book Content - ID Card Layout */}
        <div className="p-4">
          <div className="flex gap-4">
            {/* Left: Book Image */}
            <div className="flex-shrink-0">
              <div className="relative w-32 h-48 rounded-lg overflow-hidden shadow-lg border-2 border-primary-100 bg-gradient-to-br from-primary-50 to-primary-100">
                {book.imageUrl ? (
                  <img 
                    src={book.imageUrl} 
                    alt={book.title} 
                    className="w-full h-full object-contain p-2"
                    style={{ objectFit: 'contain' }}
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center p-4">
                    <FaBook className="h-12 w-12 text-primary-300" />
                    <span className="text-xs text-primary-500 mt-2">No Cover</span>
                  </div>
                )}
                {/* Status Badge */}
                <div className="absolute bottom-2 right-2 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {book.totalCopies ?? 0} copies
                </div>
              </div>
            </div>

            {/* Right: Book Details - Compact Grid */}
            <div className="flex-1 min-w-0">
              {/* Title & Author */}
              <div className="mb-3">
                <div className="flex items-start gap-2">
                  <FaBookOpen className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <h2 className="text-base font-bold text-primary-900 truncate">{book.title}</h2>
                    <p className="text-xs text-primary-600 mt-0.5 truncate">by {book.author || 'Unknown Author'}</p>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="space-y-2">
                {/* ISBN */}
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary-50 rounded-md">
                    <FaHashtag className="h-3 w-3 text-primary-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-primary-700 font-medium">ISBN</p>
                    <p className="text-sm text-primary-900 truncate">{book.isbn || '—'}</p>
                  </div>
                </div>

                {/* Category */}
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-secondary-50 rounded-md">
                    <FaTags className="h-3 w-3 text-secondary-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-secondary-700 font-medium">Category</p>
                    <p className="text-sm text-secondary-900 truncate">
                      {book.category?.name || book.categoryId || '—'}
                    </p>
                  </div>
                </div>

                {/* Author Details */}
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-accent-50 rounded-md">
                    <FaUserEdit className="h-3 w-3 text-accent-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-accent-700 font-medium">Author</p>
                    <p className="text-sm text-accent-900 truncate">{book.author || '—'}</p>
                  </div>
                </div>

                {/* Additional Info (if available) */}
             

              </div>

              {/* Quick Stats */}
              <div className="mt-4 pt-3 border-t border-primary-100">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-primary-50 rounded p-2">
                    <p className="text-xs text-primary-700">Available Copies</p>
                    <p className="text-xl font-bold text-primary-900">{book.totalCopies ?? 0}</p>
                  </div>
                  <div className="bg-secondary-50 rounded p-2">
                    <p className="text-xs text-secondary-700">Book ID</p>
                    <p className="text-sm font-mono text-secondary-900 truncate">
                      {book.id?.substring(0, 8) || '—'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="mt-4 pt-3 border-t border-primary-100">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-success-500 rounded-full"></div>
                <span className="text-xs text-primary-600">
                  {book.totalCopies && book.totalCopies > 0 ? 'Available' : 'Out of Stock'}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-xs font-medium text-primary-700 hover:text-primary-900 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={printBook}
                  className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-1"
                >
                  <FaPrint className="h-3 w-3" />
                  Print
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBookModal;